import axiosClient from "../lib/axiosClient"
import { useAuthStore } from "../stores/authStore"
import { normalizePermissionRole, ROLES } from "@/consts/permissions"

export const useAuth = () => {
  const {
    isAuthenticated,
    user,
    setAuthenticated,
    clearAuth,
    initializeAuth,
    isImpersonating,
    originalRole,
    setImpersonation,
  } = useAuthStore()

  const persistUser = (updatedUser: any) => {
    const safeUser = { ...(updatedUser || {}) }
    localStorage.setItem("auth_user", JSON.stringify(safeUser))
  }

  const getUserId = (): string | null => {
    try {
      const raw = localStorage.getItem("auth_user")
      const parsed = raw ? JSON.parse(raw) : null
      return parsed?.id ?? null
    } catch {
      return (user as any)?.id ?? null
    }
  }

  const signIn = async (username: string, password: string) => {
    try {
      const resp = await axiosClient.postForm<{
        access_token: string
        token_type?: string
        role: string | number
        role_level?: number
        id?: string
        profile_image?: string | null
        show_alerts?: boolean
        show_bw_suggestions?: boolean
      }>("auth/token/", { username, password })

      // Get token from response
      const {
        access_token,
        role,
        role_level,
        id: userId,
        profile_image,
        show_alerts,
        show_bw_suggestions,
      } = resp.data

      // Save to localStorage
      localStorage.setItem("access_token", access_token)
      localStorage.setItem("role", String(role))
      if (role_level !== undefined && role_level !== null) {
        localStorage.setItem("role_level", String(role_level))
      }

      // Create a simplified user object using backend-provided fields
      const newUser = {
        id: userId ?? username,
        username,
        permissions: [String(role)],
        profile_image: profile_image ?? null,
        show_bw_suggestions: show_bw_suggestions ?? true,
        show_alerts: show_alerts ?? true,
      }

      // Save to localStorage
      localStorage.setItem("auth_user", JSON.stringify(newUser))

      // Update reactive auth state
      setAuthenticated(true, newUser, access_token, String(role))

      return true
    } catch (error) {
      return false
    }
  }

  const signOut = () => {
    // Clear localStorage
    localStorage.removeItem("access_token")
    localStorage.removeItem("auth_user")
    localStorage.removeItem("role")
    localStorage.removeItem("role_level")
    localStorage.removeItem("apps") // legacy key from removed apps subsystem
    // Clear any dev-mode role impersonation backups
    localStorage.removeItem("original_access_token")
    localStorage.removeItem("original_role")
    localStorage.removeItem("original_role_level")

    // Clear reactive auth state
    clearAuth()

    // Redirect to login page
    window.location.href = "/login"
  }

  // === Dev-only role impersonation =======================================
  // Swap the current JWT for one minted by `POST /auth/user/act_as` with the
  // chosen role. The original developer token is backed up so the developer
  // can return to their real role at any time. A full page reload is used
  // after the swap so every cached query/SSE stream refetches with the new
  // permissions. Only meaningful when the caller's
  // real role is `developer` (the backend enforces both).
  const switchRole = async (roleLevel: number): Promise<boolean> => {
    try {
      // Make sure the API call is issued with the *real* developer JWT,
      // since `/auth/user/act_as` requires `min_role=developer`. If we are
      // already impersonating, temporarily restore the original token.
      const backupToken = localStorage.getItem("original_access_token")
      if (backupToken) {
        localStorage.setItem("access_token", backupToken)
      }

      const resp = await axiosClient.post<{
        access_token: string
        role: string
        role_level: number
      }>("/auth/user/act_as", { role: roleLevel })

      const { access_token, role, role_level } = resp.data

      // Persist the backup of the developer token & role on first swap
      if (!localStorage.getItem("original_access_token")) {
        const realToken = backupToken ?? localStorage.getItem("access_token")
        const realRole = localStorage.getItem("role")
        const realRoleLevel = localStorage.getItem("role_level")
        if (realToken) localStorage.setItem("original_access_token", realToken)
        if (realRole) localStorage.setItem("original_role", realRole)
        if (realRoleLevel)
          localStorage.setItem("original_role_level", realRoleLevel)
      }

      // Swap the active token + role
      localStorage.setItem("access_token", access_token)
      localStorage.setItem("role", String(role))
      localStorage.setItem("role_level", String(role_level))

      // Full reload so all queries, SSE and route guards see the new role
      window.location.reload()
      return true
    } catch (_) {
      // On failure, make sure we don't leave the active token swapped
      const backup = localStorage.getItem("original_access_token")
      if (backup) localStorage.setItem("access_token", backup)
      return false
    }
  }

  const stopRoleSwitch = () => {
    const token = localStorage.getItem("original_access_token")
    const role = localStorage.getItem("original_role")
    const roleLevel = localStorage.getItem("original_role_level")
    if (!token) return false

    localStorage.setItem("access_token", token)
    if (role) localStorage.setItem("role", role)
    if (roleLevel) localStorage.setItem("role_level", roleLevel)
    localStorage.removeItem("original_access_token")
    localStorage.removeItem("original_role")
    localStorage.removeItem("original_role_level")

    setImpersonation(false, null)
    window.location.reload()
    return true
  }

  const isLogged = () => {
    return isAuthenticated
  }

  // Upload profile image and persist returned URL
  const updateProfileImage = async (file: File): Promise<string | null> => {
    const formData = new FormData()
    formData.append("image", file)
    try {
      const resp = await axiosClient.post<{ profile_image?: string } | string>(
        "/auth/profile/",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      )
      const profileUrl =
        typeof resp.data === "string"
          ? resp.data
          : (resp.data?.profile_image ?? null)
      if (profileUrl) {
        const raw = localStorage.getItem("auth_user")
        const existing = raw ? JSON.parse(raw) : {}
        const updated = { ...existing, profile_image: profileUrl }
        persistUser(updated)
        // Update store user object if available
        setAuthenticated(
          true,
          updated,
          localStorage.getItem("access_token") || "",
          localStorage.getItem("role") || "",
        )
      }
      return profileUrl
    } catch (_) {
      return null
    }
  }

  // Delete profile image and clear from local storage/store
  const deleteProfileImage = async (): Promise<boolean> => {
    try {
      await axiosClient.delete("/auth/profile/")
      const raw = localStorage.getItem("auth_user")
      const existing = raw ? JSON.parse(raw) : {}
      const updated = { ...existing, profile_image: null }
      persistUser(updated)
      setAuthenticated(
        true,
        updated,
        localStorage.getItem("access_token") || "",
        localStorage.getItem("role") || "",
      )
      return true
    } catch (_) {
      return false
    }
  }

  // Update user password via PATCH /auth/user/:id
  const updateUserPassword = async (newPassword: string): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) return false
    try {
      await axiosClient.patch(`/auth/user/${uid}`, { password: newPassword })
      return true
    } catch (_) {
      return false
    }
  }

  // Update user preferences and persist in localStorage
  const updateUserPreferences = async (prefs: {
    show_bw_suggestions?: boolean
    show_alerts?: boolean
  }): Promise<boolean> => {
    const uid = getUserId()
    if (!uid) return false
    try {
      await axiosClient.patch(`/auth/user/`, prefs)
      const raw = localStorage.getItem("auth_user")
      const existing = raw ? JSON.parse(raw) : {}
      const updated = { ...existing, ...prefs }
      persistUser(updated)
      setAuthenticated(
        true,
        updated,
        localStorage.getItem("access_token") || "",
        localStorage.getItem("role") || "",
      )
      return true
    } catch (_) {
      return false
    }
  }

  return {
    signIn,
    signOut,
    isLogged,
    user,
    initializeAuth,
    updateProfileImage,
    deleteProfileImage,
    updateUserPassword,
    updateUserPreferences,
    // Dev-only role impersonation
    switchRole,
    stopRoleSwitch,
    isImpersonating,
    originalRole,
  }
}

export type AuthContext = ReturnType<typeof useAuth>

// 4 - developer, 3 - admin, 2 - engineer, 1 - user, 0 - viewer

export const isDeveloper = (): boolean => {
  return (
    normalizePermissionRole(localStorage.getItem("role")) === ROLES.DEVELOPER
  )
}

export const isAdmin = (): boolean => {
  if (isDeveloper()) return true
  return normalizePermissionRole(localStorage.getItem("role")) === ROLES.ADMIN
}
